import React, {useRef, useEffect, useState, useCallback} from 'react'
import {Box, TextField, IconButton, Tooltip, Chip, CircularProgress, Typography, Menu, MenuItem, ListSubheader} from '@mui/material'
import {
  Send as SendIcon,
  Stop as StopIcon,
  AddCircleOutline as AttachIcon,
  Mic as MicIcon,
  StopCircle as StopRecordingIcon,
  CheckCircle as CheckIcon,
  InsertDriveFile as FileIcon,
  Check as CheckMarkIcon,
  Close as CloseIcon,
  KeyboardArrowDown as CaretIcon,
} from '@mui/icons-material'
import type {CatalogModelOption, ComposerDraftStore} from './types'

interface PendingFile {
  clientId: string
  name: string
  id?: string
  uploading: boolean
}

/** Imperative surface for the parent: read/write the draft without owning its state. R3Chat uses this
 *  for voice-transcript insertion and clear-after-send. */
export interface MessageInputHandle {
  getValue: () => string
  setValue: (v: string) => void
  /** Append with a space separator (voice-accept semantics). */
  appendValue: (t: string) => void
  focus: () => void
}

interface MessageInputProps {
  // PERF: the draft text is OWNED HERE, not lifted to R3Chat. When it was a controlled prop, every
  // keystroke re-rendered R3Chat, and any identity-unstable prop downstream re-rendered the entire
  // message thread (react-markdown re-parse per character — the long-chat typing lag, confirmed by
  // CPU profile). Uncontrolled + a handle makes typing re-render exactly this component and nothing else.
  apiRef?: React.MutableRefObject<MessageInputHandle | null>
  /** Fires on every draft change (typing or imperative set) — R3Chat mirrors it into a ref for send. */
  onDraftChange?: (v: string) => void
  /** Optional shared store for the chat and selected-element composer surfaces. */
  draftStore?: ComposerDraftStore
  /** Native Ros content rendered inside the composer above the text field (for target chips). */
  contextChips?: React.ReactNode
  onSend: () => void
  onStop?: () => void
  onAttach?: (files: File[]) => void
  onRemoveFile?: (clientId: string) => void
  onMic?: () => void
  /** Temporarily disable an otherwise configured microphone (for example while another composer
   *  owns the active recording). This must stay separate from `onMic` so the UI does not describe a
   *  temporary routing state as "coming soon". */
  voiceDisabled?: boolean
  voiceDisabledReason?: string
  disabled?: boolean
  isStreaming?: boolean
  isRecording?: boolean
  isVoiceConnecting?: boolean
  voiceTranscript?: string
  /** Live mic level 0–1 as a REF (updated at frame rate by useVoiceInput without re-rendering the
   *  tree); the Waveform's own rAF loop reads it each frame. */
  audioLevelRef?: React.MutableRefObject<number>
  /** ✓ accept the transcript (fills the input). */
  onVoiceAccept?: () => void
  /** ✕ discard the transcript. */
  onVoiceCancel?: () => void
  pendingFiles?: PendingFile[]
  /** The model currently in use for this session. Dev-mode only — see R3Chat's devModelLabel. */
  modelLabel?: string
  /** Dev-mode-only model picker. Both must be supplied to make the badge clickable; without them
   *  (i.e. for any consumer that hasn't wired dev mode) it falls back to a static, non-interactive
   *  label — model identity stays hidden from regular users regardless. */
  availableModels?: CatalogModelOption[]
  onSelectModel?: (spec: string) => void
  /** 'bar' = bottom chat bar (top border, full chrome — default). 'hero' = clean empty-state hero
   *  input: no top border, a separate labelled "Send" button beside the box (matches the mock). */
  variant?: 'bar' | 'hero'
}

// .zip: micro-app / Extension package import via chat (import_micro_app tool).
const ACCEPTED_EXTENSIONS = '.docx,.xlsx,.csv,.tsv,.pdf,.pptx,.xls,.doc,.jpg,.jpeg,.png,.gif,.svg,.webp,.mp4,.webm,.mov,.html,.htm,.yaml,.yml,.txt,.zip'

// Animated recording waveform — a row of cyan bars forming a wave that TRAVELS left→right (the crest
// moves across bars as `phase` advances), with overall amplitude driven by the live mic `level` so it
// reacts to the voice. Fills the available width inline in the action row.
const WAVE_BARS = 40
const Waveform: React.FC<{levelRef?: React.MutableRefObject<number>; active: boolean}> = ({levelRef, active}) => {
  const [phase, setPhase] = React.useState(0)
  React.useEffect(() => {
    if (!active) return
    let raf = 0
    const loop = () => { setPhase(p => p + 0.22); raf = requestAnimationFrame(loop) }
    raf = requestAnimationFrame(loop)
    return () => cancelAnimationFrame(raf)
  }, [active])
  // amp ramps with mic level but keeps a gentle idle motion so it's never flat. The level arrives via
  // ref (no tree re-render at frame rate); this component re-renders itself each frame via `phase`,
  // so reading the ref here picks up the fresh value every frame.
  const amp = 0.3 + 0.7 * Math.min(1, levelRef?.current ?? 0)
  return (
    <Box sx={{flex: 1, display: 'flex', alignItems: 'center', gap: '2px', height: 24, overflow: 'hidden'}}>
      {Array.from({length: WAVE_BARS}).map((_, i) => {
        // Subtracting i*spacing from phase makes the crest travel to the RIGHT over time.
        const wave = (Math.sin(phase - i * 0.45) + 1) / 2 // 0..1, shifts across bars each frame
        const h = 3 + amp * wave * 18                       // 3..~21px
        return <Box key={i} sx={{flex: 1, minWidth: 2, maxWidth: 4, borderRadius: 2, bgcolor: 'primary.main', height: `${h}px`}} />
      })}
    </Box>
  )
}

const MessageInput: React.FC<MessageInputProps> = ({
  apiRef,
  onDraftChange,
  draftStore,
  contextChips,
  onSend,
  onStop,
  onAttach,
  onRemoveFile,
  onMic,
  voiceDisabled = false,
  voiceDisabledReason,
  disabled,
  isStreaming,
  isRecording,
  isVoiceConnecting,
  voiceTranscript,
  audioLevelRef,
  onVoiceAccept,
  onVoiceCancel,
  pendingFiles = [],
  modelLabel,
  availableModels,
  onSelectModel,
  variant = 'bar'
}) => {
  const isHero = variant === 'hero'
  const inputRef = useRef<HTMLInputElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [modelMenuAnchor, setModelMenuAnchor] = useState<HTMLElement | null>(null)

  // Self-owned draft (see MessageInputProps note). textRef mirrors state so the imperative handle can
  // read synchronously; onDraftChange goes through a ref so the handle registration never re-runs.
  const [text, setText] = useState(() => draftStore?.getSnapshot() ?? '')
  const textRef = useRef(draftStore?.getSnapshot() ?? '')
  const onDraftChangeRef = useRef(onDraftChange)
  onDraftChangeRef.current = onDraftChange
  const updateText = useCallback((v: string) => {
    textRef.current = v
    setText(v)
    draftStore?.setValue(v)
    onDraftChangeRef.current?.(v)
  }, [draftStore])

  useEffect(() => {
    if (!draftStore) return
    const sync = () => {
      const value = draftStore.getSnapshot()
      textRef.current = value
      setText(value)
      onDraftChangeRef.current?.(value)
    }
    sync()
    return draftStore.subscribe(sync)
  }, [draftStore])

  useEffect(() => {
    if (!apiRef) return
    apiRef.current = {
      getValue: () => textRef.current,
      setValue: updateText,
      appendValue: (t: string) => updateText(textRef.current ? `${textRef.current} ${t}` : t),
      focus: () => inputRef.current?.focus(),
    }
    return () => { apiRef.current = null }
  }, [apiRef, updateText])

  useEffect(() => {
    if (!disabled) {
      inputRef.current?.focus()
    }
  }, [disabled])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      const hasText = text.trim().length > 0
      const hasFiles = pendingFiles.some(f => f.id)
      if (!disabled && (hasText || hasFiles)) {
        onSend()
      }
    }
  }

  const handleFileSelect = useCallback((files: FileList | null) => {
    if (!files || !onAttach) return
    onAttach(Array.from(files))
  }, [onAttach])

  const handleAttachClick = useCallback(() => {
    fileInputRef.current?.click()
  }, [])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    if (!onAttach) return
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }, [onAttach])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
    if (!onAttach) return
    handleFileSelect(e.dataTransfer.files)
  }, [onAttach, handleFileSelect])

  const attachEnabled = !!onAttach

  return (
    <Box
      sx={{ position: 'relative' }}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Drop zone overlay */}
      {isDragging && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            bgcolor: 'primary.main',
            opacity: 0.08,
            borderRadius: 2,
            border: '2px dashed',
            borderColor: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10,
            pointerEvents: 'none'
          }}
        />
      )}

      {/* File chips */}
      {pendingFiles.length > 0 && (
        <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1}}>
          {pendingFiles.map(f => (
            <Chip
              key={f.clientId}
              icon={f.uploading ? <CircularProgress size={14} /> : (f.id ? <CheckIcon fontSize="small" color="success" /> : <FileIcon fontSize="small" />)}
              label={f.name}
              size="small"
              variant="outlined"
              onDelete={onRemoveFile ? () => onRemoveFile(f.clientId) : undefined}
              deleteIcon={onRemoveFile ? <CloseIcon aria-label={`Remove ${f.name}`} /> : undefined}
              sx={{maxWidth: 200}}
            />
          ))}
        </Box>
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={ACCEPTED_EXTENSIONS}
        style={{display: 'none'}}
        onChange={e => {
          handleFileSelect(e.target.files)
          e.currentTarget.value = ''
        }}
      />

      {(() => {
        const canSend = !disabled && (text.trim().length > 0 || pendingFiles.filter(f => f.id).length > 0)
        const attachBtn = (
          <Tooltip title={attachEnabled ? 'Attach file' : 'Attach file (coming soon)'}>
            <span>
              <IconButton
                size="small"
                onClick={attachEnabled ? handleAttachClick : undefined}
                disabled={!attachEnabled}
                sx={{ color: attachEnabled ? 'text.secondary' : 'action.disabled', p: 0.5, '&:hover': { color: 'primary.main' } }}
              >
                <AttachIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </span>
          </Tooltip>
        )
        const micBtn = (
          <Tooltip title={isRecording
            ? 'Stop recording'
            : isVoiceConnecting
              ? 'Connecting...'
              : !onMic
                ? 'Voice input (coming soon)'
                : voiceDisabled
                  ? voiceDisabledReason ?? 'Voice input is temporarily unavailable'
                  : 'Voice input'}>
            <span>
              <IconButton
                size="small"
                // Do not pass React's click event through this capability callback. R3Chat's voice
                // handler also accepts an optional ComposerDraftStore for the website editor; the
                // event was therefore mistaken for an external draft destination.
                onClick={onMic ? () => onMic() : undefined}
                disabled={!onMic || voiceDisabled || isVoiceConnecting || disabled}
                sx={{
                  p: 0.5,
                  color: isRecording ? 'error.main' : 'text.secondary',
                  '&:hover': { color: onMic ? 'primary.main' : undefined },
                  ...(isRecording && {
                    animation: 'pulse-recording 1.5s ease-in-out infinite',
                    '@keyframes pulse-recording': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.5 } }
                  })
                }}
              >
                {isRecording ? <StopRecordingIcon sx={{ fontSize: 20 }} /> : <MicIcon sx={{ fontSize: 20 }} />}
              </IconButton>
            </span>
          </Tooltip>
        )
        // Send: just the ICON, colored by state (cyan/primary when active, muted when empty) — no
        // background pill. Stop (while streaming) is the red variant.
        const sendBtn = isStreaming && onStop ? (
          <Tooltip title="Stop generating">
            <IconButton onClick={onStop} sx={{ p: 0.5, color: 'error.main', '&:hover': { color: 'error.dark' } }}>
              <StopIcon sx={{ fontSize: 22 }} />
            </IconButton>
          </Tooltip>
        ) : (
          <Tooltip title="Send (Enter)">
            <span>
              <IconButton
                onClick={onSend}
                disabled={!canSend}
                sx={{
                  p: 0.5,
                  color: canSend ? 'primary.main' : 'action.disabled',
                  '&:hover': { color: canSend ? 'primary.dark' : 'action.disabled' },
                  transition: 'color 0.15s',
                }}
              >
                <SendIcon sx={{ fontSize: 22 }} />
              </IconButton>
            </span>
          </Tooltip>
        )

        const textField = (
          <TextField
            inputRef={inputRef}
            fullWidth
            multiline
            maxRows={isHero ? 8 : 4}
            value={text}
            onChange={(e) => updateText(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={disabled || isRecording}
            placeholder={isRecording && voiceTranscript ? voiceTranscript : isRecording ? 'Listening...' : isHero ? 'Ask Ros anything…' : 'Ask Ros anything... (Shift+Enter for new line)'}
            variant="standard"
            InputProps={{ disableUnderline: true }}
            sx={{
              '& .MuiInputBase-root': { fontSize: isHero ? '0.95rem' : '0.9rem', lineHeight: 1.6, py: 0.5, fontFamily: '"Source Sans 3", "Inter", sans-serif' },
              '& .MuiInputBase-input': { fontFamily: '"Source Sans 3", "Inter", sans-serif', fontSize: isHero ? '0.95rem' : '0.9rem' }
            }}
          />
        )

        const modelName = modelLabel?.replace(/^Model:\s*/, '')
        // Interactive only when the caller supplies both the catalog and a select handler (dev mode,
        // see R3Chat's devModelLabel) — otherwise a plain static label, same as before this existed.
        const canPickModel = Boolean(availableModels?.length && onSelectModel)
        const modelSelectorBox = modelName ? (
          <Box
            component={canPickModel ? 'button' : 'span'}
            type={canPickModel ? 'button' : undefined}
            aria-label={`Selected model: ${modelName}`}
            onClick={canPickModel ? (e: React.MouseEvent<HTMLElement>) => setModelMenuAnchor(e.currentTarget) : undefined}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.15,
              maxWidth: 180,
              color: 'text.secondary',
              fontSize: '0.8rem',
              fontWeight: 500,
              lineHeight: 1,
              userSelect: 'none',
              cursor: canPickModel ? 'pointer' : 'default',
              border: 0,
              p: 0,
              bgcolor: 'transparent',
              font: 'inherit',
              '&:hover': canPickModel ? { color: 'text.primary' } : undefined,
            }}
          >
            <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {modelName}
            </Box>
            <CaretIcon sx={{ fontSize: 16, flexShrink: 0, color: 'text.secondary' }} />
          </Box>
        ) : null
        const modelSelector = modelSelectorBox ? (
          canPickModel ? (
            <>
              <Tooltip title="Dev mode: override the model for this session">{modelSelectorBox}</Tooltip>
              <Menu anchorEl={modelMenuAnchor} open={Boolean(modelMenuAnchor)} onClose={() => setModelMenuAnchor(null)}>
                {(['proprietary', 'open-weight'] as const).flatMap(group => {
                  const groupModels = (availableModels ?? []).filter(m => (group === 'open-weight') === m.openWeight)
                  if (!groupModels.length) return []
                  return [
                    <ListSubheader key={`${group}-header`} sx={{lineHeight: '32px'}}>
                      {group === 'open-weight' ? 'Open Weight' : 'Proprietary'}
                    </ListSubheader>,
                    ...groupModels.map(m => (
                      <MenuItem
                        key={m.id}
                        disabled={!m.available}
                        onClick={() => { onSelectModel?.(m.spec); setModelMenuAnchor(null) }}
                      >
                        {m.label} ({m.vendor}){m.available ? '' : ' (not configured)'}
                      </MenuItem>
                    )),
                  ]
                })}
              </Menu>
            </>
          ) : (
            <Tooltip title="Model switching is not available yet">{modelSelectorBox}</Tooltip>
          )
        ) : null

        // RECORDING state — SAME tall box as the composer (no size change): "Listening…" sits where
        // the message would, and the bottom row carries the waveform + ✕ (discard) / ✓ (accept),
        // replacing the attach/mic/send controls.
        if (isRecording || isVoiceConnecting) {
          return (
            <Box
              sx={{
                display: 'flex', flexDirection: 'column', gap: isHero ? 1 : 0.5,
                border: 1, borderColor: 'primary.main', borderRadius: 3,
                px: 2, py: isHero ? 1.5 : 1, bgcolor: 'background.paper',
                boxShadow: '0 0 0 2px rgba(0, 143, 168, 0.15)',
              }}
            >
              {contextChips}
              {/* Show the live transcript as it streams in; only fall back to the placeholder while
                  there's nothing transcribed yet (connecting, or before the first words arrive). */}
              <Typography sx={{
                fontSize: isHero ? '0.95rem' : '0.9rem', py: 0.5, minHeight: '1.6em',
                lineHeight: 1.6, fontFamily: '"Source Sans 3", "Inter", sans-serif',
                color: voiceTranscript ? 'text.primary' : 'text.secondary',
              }}>
                {voiceTranscript || (isVoiceConnecting ? 'Connecting…' : 'Listening…')}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Waveform levelRef={audioLevelRef} active={!!isRecording} />
                <Tooltip title="Discard">
                  <IconButton onClick={onVoiceCancel ? () => onVoiceCancel() : undefined} sx={{ p: 0.5, color: 'text.secondary', '&:hover': { color: 'error.main' } }}>
                    <CloseIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Use transcript">
                  <span>
                    <IconButton onClick={onVoiceAccept ? () => onVoiceAccept() : undefined} disabled={isVoiceConnecting} sx={{ p: 0.5, color: 'primary.main', '&:hover': { color: 'primary.dark' } }}>
                      <CheckMarkIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Box>
          )
        }

        // ONE composer layout for both states (Claude-style): a box with the text on top and a
        // bottom action row (attach left, mic + cyan send right). Hero is taller/roomier; the bottom
        // bar is the same pattern, just a touch more compact — so the chat input is consistent
        // everywhere instead of the old cramped single-row pill.
        return (
          <Box
            sx={{
              display: 'flex', flexDirection: 'column', gap: isHero ? 1 : 0.5,
              border: 1, borderColor: isDragging ? 'primary.main' : 'divider', borderRadius: 3,
              px: 2, py: isHero ? 1.5 : 1, bgcolor: 'background.paper',
              '&:focus-within': { borderColor: 'primary.main', boxShadow: '0 0 0 2px rgba(0, 143, 168, 0.15)' },
              transition: 'border-color 0.15s, box-shadow 0.15s',
            }}
          >
            {contextChips}
            {textField}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
              {attachBtn}
              <Box sx={{ flex: 1 }} />
              {modelSelector}
              {micBtn}
              {sendBtn}
            </Box>
          </Box>
        )
      })()}
    </Box>
  )
}

export default MessageInput
